import React, { useRef } from "react";
import Modal from "./Modal";
import { FiPrinter, FiX, FiCheckCircle, FiFileText, FiCalendar, FiUser, FiTruck, FiHash } from "react-icons/fi";

const ReturnVoucherModal = ({ isOpen, onClose, returnData, type = "purchase" }) => {
  const printRef = useRef(null);

  if (!isOpen || !returnData) return null;

  const isPurchase = type === "purchase";
  const title = isPurchase ? "Purchase Return Debit Note" : "Sale Return Credit Note";
  const partyLabel = isPurchase ? "Vendor / Supplier" : "Customer / Client";
  const partyName = isPurchase
    ? returnData.vendor_name || returnData.vendor?.company_name || "—"
    : returnData.client_name || returnData.client?.company_name || "—";
  const partyContact = isPurchase
    ? returnData.vendor_contact || returnData.vendor?.contact || ""
    : returnData.client_contact || returnData.client?.contact || "";
  const partyPerson = isPurchase
    ? returnData.vendor_contact_person || returnData.vendor?.contact_person_name || ""
    : returnData.client_contact_person || returnData.client?.contact_person_name || "";
  const partyAddress = isPurchase
    ? returnData.vendor_address || returnData.vendor?.address || ""
    : returnData.client_address || returnData.client?.address || "";
  const partyNtn = isPurchase
    ? returnData.vendor_ntn || returnData.vendor?.ntn || ""
    : returnData.client_ntn || returnData.client?.ntn || "";

  const refNumber = isPurchase ? returnData.bill_number : returnData.invoice_number;
  const refLabel = isPurchase ? "Original Purchase Bill #" : "Original Sale Invoice #";

  const items = returnData.items || [];
  const totalQty = items.reduce((sum, it) => sum + (parseInt(it.quantity) || 1), 0);
  const totalAmount = parseFloat(returnData.total_amount) || items.reduce((sum, it) => sum + (parseFloat(it.total_price || (it.unit_price * (it.quantity || 1))) || 0), 0);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="850px">
      <div style={{ padding: "10px 0" }}>
        {/* Printable Area */}
        <div
          ref={printRef}
          className="printable-return-voucher"
          style={{
            backgroundColor: "#fff",
            borderRadius: 8,
            border: "1px solid var(--slate-200)",
            padding: "28px 32px",
            fontFamily: "Inter, sans-serif",
            color: "var(--navy, #0F172A)",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              borderBottom: "2px solid var(--slate-200)",
              paddingBottom: 20,
              marginBottom: 20,
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-block",
                  padding: "4px 10px",
                  borderRadius: 6,
                  backgroundColor: isPurchase ? "#FEF3C7" : "#DCFCE7",
                  color: isPurchase ? "#B45309" : "#15803D",
                  fontSize: "0.78rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: 8,
                }}
              >
                {isPurchase ? "Vendor Return Voucher" : "Customer Return Credit Note"}
              </div>
              <h2 style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                {title}
              </h2>
              <div style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginTop: 4 }}>
                Commercial IT Inventory Management System
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: isPurchase ? "#B45309" : "var(--primary, #064E3B)" }}>
                {returnData.return_number}
              </div>
              <div style={{ fontSize: "0.82rem", color: "var(--slate-500)", marginTop: 4 }}>
                <strong>Date:</strong> {returnData.return_date || new Date().toISOString().slice(0, 10)}
              </div>
              {returnData.created_by_name && (
                <div style={{ fontSize: "0.78rem", color: "var(--slate-400)", marginTop: 2 }}>
                  Processed by: {returnData.created_by_name}
                </div>
              )}
            </div>
          </div>

          {/* Reference and Party Details */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              backgroundColor: "var(--slate-50)",
              borderRadius: 8,
              padding: "16px 20px",
              marginBottom: 24,
            }}
          >
            <div>
              <div style={{ fontSize: "0.75rem", textTransform: "uppercase", color: "var(--slate-500)", fontWeight: 700, marginBottom: 4 }}>
                {partyLabel}
              </div>
              <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--navy)" }}>
                {partyName}
              </div>
              {partyPerson && (
                <div style={{ fontSize: "0.82rem", color: "var(--slate-600)", marginTop: 2 }}>
                  Attn: {partyPerson}
                </div>
              )}
              {partyContact && (
                <div style={{ fontSize: "0.82rem", color: "var(--slate-600)" }}>
                  Phone: {partyContact}
                </div>
              )}
              {partyAddress && (
                <div style={{ fontSize: "0.78rem", color: "var(--slate-500)", marginTop: 2 }}>
                  {partyAddress}
                </div>
              )}
              {partyNtn && (
                <div style={{ fontSize: "0.78rem", color: "var(--slate-500)" }}>
                  NTN: {partyNtn}
                </div>
              )}
            </div>

            <div>
              <div style={{ fontSize: "0.75rem", textTransform: "uppercase", color: "var(--slate-500)", fontWeight: 700, marginBottom: 4 }}>
                Reference Details
              </div>
              <div style={{ fontSize: "0.92rem", fontWeight: 700, color: "var(--navy)" }}>
                {refLabel}: <span style={{ color: "var(--primary)", fontFamily: "monospace" }}>{refNumber || "N/A"}</span>
              </div>
              <div style={{ fontSize: "0.84rem", color: "var(--slate-600)", marginTop: 6 }}>
                <strong>Return Date:</strong> {returnData.return_date || "—"}
              </div>
              {returnData.reason && (
                <div style={{ fontSize: "0.82rem", color: "var(--slate-600)", marginTop: 4 }}>
                  <strong>Reason:</strong> <span style={{ fontStyle: "italic" }}>{returnData.reason}</span>
                </div>
              )}
            </div>
          </div>

          {/* Returned Items Table */}
          <div style={{ marginBottom: 24, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ backgroundColor: "var(--slate-100)", borderBottom: "2px solid var(--slate-200)", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px", width: 40 }}>#</th>
                  <th style={{ padding: "10px 12px" }}>Item Description</th>
                  <th style={{ padding: "10px 12px" }}>Serial Number</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", width: 70 }}>Qty</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", width: 110 }}>Unit Price</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", width: 120 }}>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: 20, textAlign: "center", color: "var(--slate-400)" }}>
                      No items listed in this return
                    </td>
                  </tr>
                ) : (
                  items.map((it, idx) => {
                    const qty = parseInt(it.quantity) || 1;
                    const uPrice = parseFloat(it.unit_price) || 0.0;
                    const tPrice = parseFloat(it.total_price) || qty * uPrice;

                    return (
                      <tr key={it.id || idx} style={{ borderBottom: "1px solid var(--slate-100)" }}>
                        <td style={{ padding: "10px 12px", color: "var(--slate-500)" }}>{idx + 1}</td>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                          {it.item_name}
                          {it.category_name && (
                            <span style={{ fontSize: "0.75rem", color: "var(--slate-400)", display: "block" }}>
                              {it.category_name} {it.subcategory_name ? `• ${it.subcategory_name}` : ""}
                            </span>
                          )}
                          {it.reason && (
                            <span style={{ fontSize: "0.75rem", color: "#B45309", display: "block" }}>
                              Reason: {it.reason}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px", fontFamily: "monospace", color: it.serial_number ? "var(--primary)" : "var(--slate-400)" }}>
                          {it.serial_number || "— Non-Serial —"}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700 }}>
                          {qty}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          PKR {uPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>
                          PKR {tPrice.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "2px solid var(--slate-300)", backgroundColor: "var(--slate-50)" }}>
                  <td colSpan={3} style={{ padding: "12px", fontWeight: 800, textAlign: "right" }}>
                    Total Returned Items: {totalQty}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center", fontWeight: 800 }}>
                    {totalQty}
                  </td>
                  <td style={{ padding: "12px", textAlign: "right", fontWeight: 700, color: "var(--slate-600)" }}>
                    Grand Total:
                  </td>
                  <td style={{ padding: "12px", textAlign: "right", fontWeight: 800, fontSize: "1.05rem", color: isPurchase ? "#B45309" : "var(--primary)" }}>
                    PKR {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Signatures */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 40, paddingTop: 20, borderTop: "1px dashed var(--slate-200)" }}>
            <div style={{ textAlign: "center", minWidth: 180 }}>
              <div style={{ borderBottom: "1px solid var(--slate-400)", height: 35 }}></div>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-600)", marginTop: 6 }}>
                Prepared By
              </div>
            </div>

            <div style={{ textAlign: "center", minWidth: 180 }}>
              <div style={{ borderBottom: "1px solid var(--slate-400)", height: 35 }}></div>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-600)", marginTop: 6 }}>
                Verified / Approved By
              </div>
            </div>

            <div style={{ textAlign: "center", minWidth: 180 }}>
              <div style={{ borderBottom: "1px solid var(--slate-400)", height: 35 }}></div>
              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-600)", marginTop: 6 }}>
                Received By ({partyLabel})
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "9px 18px",
              borderRadius: 8,
              border: "1px solid var(--slate-300)",
              backgroundColor: "#fff",
              color: "var(--slate-700)",
              fontWeight: 600,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handlePrint}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 22px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "var(--primary, #064E3B)",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            <FiPrinter size={16} /> Print / Save PDF
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReturnVoucherModal;
